import {
  MoreHorizontal,
  Trash2,
  Edit2,
  ClipboardList,
  Clock,
  MessageSquare,
  Globe,
  FileLock,
} from "lucide-react";
import { useState } from "react";
import { Card } from "../ui/Card";
import { TableActionMenu } from "../ui/TableActionMenu";
import { useAuth } from "../../contexts/AuthContext";

// Temporary type definition if strict types aren't available yet in dashboard
interface QuizType {
  _id: string;
  title: string;
  course: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  questions: string[];
  duration: number;
  isPublished: boolean;
  totalAttempts: number;
  averageScore: number;
  createdAt: string;
}

interface ExamsListTableProps {
  exams: QuizType[];
  isLoading: boolean;
  onEdit: (exam: QuizType) => void;
  onDelete: (exam: QuizType) => void;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onTogglePublish?: (exam: QuizType) => void;
}

export function ExamsListTable({
  exams,
  isLoading,
  onEdit,
  onDelete,
  totalItems,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  onTogglePublish,
}: ExamsListTableProps) {
  const { hasPermission } = useAuth();

  const canUpdate = hasPermission('quizzes:update');
  const canDelete = hasPermission('quizzes:delete');
  const canPublish = hasPermission('quizzes:publish');

  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);

  if (isLoading) {
    return (
      <Card className="p-0 border shadow-sm">
        <div className="p-8 text-center text-gray-500">
          <div className="animate-spin inline-block w-6 h-6 border-[3px] border-current border-t-transparent text-indigo-600 rounded-full" />
          <p className="mt-2">Loading exams...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-0 border shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-[1000px] w-full text-left text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                Exam Title
              </th>
              <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                Course
              </th>
              <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                Duration
              </th>
              <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                Questions
              </th>
              <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                Stats
              </th>
              <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                Status
              </th>
              <th className="px-3 sm:px-6 py-4 w-[50px]"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {exams.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 sm:px-6 py-12 text-center text-gray-500"
                >
                  <div className="flex flex-col items-center justify-center">
                    <ClipboardList className="w-10 h-10 text-gray-300 mb-2" />
                    <p>No exams found.</p>
                  </div>
                </td>
              </tr>
            ) : (
              exams.map((exam) => (
                <tr
                  key={exam._id}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-3 sm:px-6 py-4">
                    <span
                      className="font-medium text-[#102040] block truncate max-w-[200px]"
                      title={exam.title}
                    >
                      {exam.title}
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-4 text-gray-600">
                    {exam.course?.title || "N/A"}
                  </td>
                  <td className="px-3 sm:px-6 py-4">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Clock size={14} />
                      <span>{exam.duration} min</span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-4">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <MessageSquare size={14} />
                      <span>{exam.questions?.length || 0}</span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-4">
                    <div className="flex flex-col text-xs text-gray-500">
                      <span>Attempts: {exam.totalAttempts || 0}</span>
                      <span>Avg Score: {exam.averageScore || 0}%</span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        exam.isPublished
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {exam.isPublished ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-4 text-right relative">
                    <button
                      className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeMenu?.id === exam._id) {
                          setActiveMenu(null);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setActiveMenu({
                            id: exam._id,
                            position: {
                              top: rect.bottom,
                              right: window.innerWidth - rect.right,
                            },
                          });
                        }
                      }}
                    >
                      <MoreHorizontal size={20} />
                    </button>

                    <TableActionMenu
                      isOpen={activeMenu?.id === exam._id}
                      onClose={() => setActiveMenu(null)}
                      position={activeMenu?.position || null}
                    >
                      {canUpdate && (
                        <button
                          onClick={() => {
                            onEdit(exam);
                            setActiveMenu(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                        >
                          <Edit2 size={14} className="text-blue-500" />
                          Edit Exam
                        </button>
                      )}
                      {canPublish && onTogglePublish && (
                        <button
                          onClick={() => {
                            onTogglePublish(exam);
                            setActiveMenu(null);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                            exam.isPublished
                              ? "text-amber-600 hover:bg-amber-50"
                              : "text-emerald-600 hover:bg-emerald-50"
                          }`}
                        >
                          {exam.isPublished ? (
                            <FileLock size={14} />
                          ) : (
                            <Globe size={14} />
                          )}
                          {exam.isPublished ? "Unpublish" : "Publish"}
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => {
                            onDelete(exam);
                            setActiveMenu(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      )}
                    </TableActionMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="px-3 sm:px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm text-gray-500">
          <span>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} to{" "}
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems} exams
          </span>
          <div className="flex gap-1 ml-4">
            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            >
              &lt; Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let p = i + 1;
              if (totalPages > 5 && currentPage > 3) {
                p = currentPage - 2 + i;
              }
              if (p > totalPages) return null;

              return (
                <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  className={`px-3 py-1 rounded ${
                    currentPage === p
                      ? "bg-gray-900 text-white"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === totalPages}
              onClick={() =>
                onPageChange(Math.min(totalPages, currentPage + 1))
              }
            >
              Next &gt;
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
